import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState
} from 'react';
import { hot } from 'react-hot-loader';
import { useMutation, useLazyQuery } from '@apollo/client';
import { connect } from 'react-redux';
import { getLongFromShortCode } from '@homeaway/google-address-parser';
import { TrackingContext } from '@vrbo/react-event-tracking';
import {
    defaultProps, mapDispatchToProps, mapStateToProps, proptypes
} from './properties';
import { CREATE_JOIN_MUTATION, FETCH_SALESFORCE_INFORMATION, FETCH_SUBMARKET_ID } from './graphql/query';
import withPageFlow from '../../components/PageFlow/PageFlow';
import { UserCtx } from '../user';
import { AAPPages, AGENTIC_LANDING_PAGE, JOIN_HANDLER } from '../../components/PageFlow/PageList';
import { buildContactPayload, buildSalesforceInformationPayload } from './utils';
import { getLypPropertyByAddress } from '../../actions/graphQlSvc';

import JelCallbackFns from './utils/JELflow';
import AapCallbackFns from './utils/AAPflow';
import { ExperimentCtx } from '../experiments';
import { EGTNL_TESTS_KUMO } from '../../../server/api/abacus/constants';
import log from '../../utils/ClientLogger';
import { createOutcomeEvent } from '../../utils/EventProviders/EgClickstreamUtil';
import useDidMountEffect from '../../utils/hooks/useDidMountEffect';

export const JoinCtx = createContext();

export const JOIN_SOURCES = {
    JOIN: 'JOIN',
    AAP: 'ADDPROPERTY',
    AGENTIC_ONBOARDING_JEL: 'AGENTIC_ONBOARDING_JEL',
    AGENTIC_ONBOARDING_AAP: 'AGENTIC_ONBOARDING_AAP',
    AGENTIC_ONBOARDING: 'AGENTIC_ONBOARDING'
};

const isAAPFlow = (source) => source === JOIN_SOURCES.AAP;

export function JoinHandler(props) {
    const {
        children
    } = props;

    const { epcUserId, paperContractUser } = useContext(UserCtx);
    const { experimentEvaluations } = useContext(ExperimentCtx);
    const {
        [EGTNL_TESTS_KUMO.ULX_SALESFORCE_IN_LYPS]: lypsSalesforceExperiment,
        [EGTNL_TESTS_KUMO.SHARED_SOURCE_AGENTIC_ONBOARDING]: isAgentic
    } = experimentEvaluations || {};
    const { trigger } = useContext(TrackingContext);

    const [loading, setLoading] = useState(false);

    // eslint-disable-next-line no-unused-vars
    const [createContact, createContactState] = useMutation(CREATE_JOIN_MUTATION);

    const [submarketIdResponse, setSubmarketIdResponse] = useState('');
    const { setSubmarketId } = props;
    useDidMountEffect(() => {
        setSubmarketId(submarketIdResponse);
    }, [submarketIdResponse]);

    const onCompleteSubmarketId = ({ fetchSubmarketId }) => {
        setSubmarketIdResponse(fetchSubmarketId);
    };
    const onErrorSubmarketId = (error) => {
        log(['error'], 'FetchSubmarketId', error);
        setSubmarketIdResponse('');
    };

    const [fetchSubmarketIdCall, fetchSubmarketIdState] = useLazyQuery(FETCH_SUBMARKET_ID, {
        onCompleted: onCompleteSubmarketId,
        onError: onErrorSubmarketId
    });

    const createJoin = async ({
        source,
        pageName
    }) => {
        const { setJoinId, setPropertyId, setEligibilityStatus } = props;
        setJoinId('');
        setPropertyId('');
        setEligibilityStatus('');
        const { managerInfo, chainInfo } = props;
        const contact = buildContactPayload({
            ...props,
            source,
            epcUserId,
            isPaperContractUser: paperContractUser,
            managerInfo: isAAPFlow(source) ? 'no' : managerInfo,
            chainInfo: isAAPFlow(source) ? 'no' : chainInfo,
            lypsSalesforceExperiment
        });

        const {
            onComplete,
            onError
        } = source === JOIN_SOURCES.JOIN ? JelCallbackFns : AapCallbackFns;
        await createContact({
            variables: {
                contact
            }
        }).then(
            onComplete({ ...props, pageName, trigger }),
            onError({ ...props, trigger, pageName })
        ).catch(onError({ ...props, trigger, pageName }));
    };

    const callPropertyCrawler = useCallback(async () => {
        const {
            address,
            latLngDetails: coordinates,
            propertyName,
            latinPropertyName,
            websiteUrl,
            placeId
        } = props;
        return getLypPropertyByAddress({
            street: address.address1,
            city: address.city,
            state: address.stateProvince,
            zip: address.postalCode,
            country: address.country,
            latitude: coordinates?.latitude ?? 0.0,
            longitude: coordinates?.longitude ?? 0.0,
            propertyName: propertyName || latinPropertyName,
            latinPropertyName,
            websiteUrl,
            placeId
        });
    /* eslint-disable react/destructuring-assignment */
    }, [
        props.address,
        props.latLngDetails,
        props.propertyName,
        props.latinPropertyName,
        props.websiteUrl,
        props.placeId
    ]);
    /* eslint-enable react/destructuring-assignment */

    const fetchPropertyIdFallback = async () => {
        log(['warn'], 'AgenticJoin-PropertyIdFallback', { message: 'propertyId missing from Redux, falling back to crawler' });
        let lypPropertyData = {};
        try {
            lypPropertyData = await callPropertyCrawler();
        } catch (error) {
            const { setCreateAccountFail, onSubmitError } = props;
            trigger('outcome', createOutcomeEvent('user_account_creation.failed', 'AgenticJoin', 'AddressLookup'));
            setCreateAccountFail(error.message);
            onSubmitError(error.message);
            return null;
        }
        return lypPropertyData.propertyId;
    };

    const createAgenticJoin = async ({ source, pageName }) => {
        const {
            setJoinId,
            setPropertyId,
            setEligibilityStatus,
            propertyId: reduxPropertyId
        } = props;
        setJoinId('');
        setPropertyId('');
        setEligibilityStatus('');
        let propertyId = reduxPropertyId;
        if (!propertyId) {
            const fallbackPropertyId = await fetchPropertyIdFallback();
            if (fallbackPropertyId === null) return;
            propertyId = fallbackPropertyId;
        }
        const contact = {
            ...buildContactPayload({
                ...props,
                managerInfo: 'no',
                chainInfo: 'no',
                source,
                epcUserId,
                isPaperContractUser: paperContractUser,
                lypsSalesforceExperiment,
                version: 'v3'
            }),
            propertyId: propertyId || undefined
        };
        const { onComplete, onError } = JelCallbackFns;
        await createContact({
            variables: {
                contact
            }
        }).then(
            onComplete({ ...props, pageName, trigger }),
            onError({ ...props, trigger, pageName })
        ).catch(onError({ ...props, trigger, pageName }));
    };

    const fetchSubmarketId = async () => {
        const { address, latLngDetails } = props;
        const result = await fetchSubmarketIdCall({
            variables: {
                address: {
                    country: getLongFromShortCode(address?.country.toUpperCase()),
                    latitude: latLngDetails.latitude,
                    longitude: latLngDetails.longitude
                },
                lypsIntegration: lypsSalesforceExperiment === 1
            }
        });
        return result?.data?.fetchSubmarketId;
    };

    const { setIsSelfServe } = props;
    const [isSelfServeResponse, setIsSelfServeResponse] = useState('');
    useDidMountEffect(() => {
        setIsSelfServe(isSelfServeResponse);
    }, [isSelfServeResponse]);
    const onCompleteSalesforceInformation = ({ fetchSalesforceInformation }) => {
        setIsSelfServeResponse(fetchSalesforceInformation.isSelfServe);
        setSubmarketIdResponse(fetchSalesforceInformation.submarketId);
    };
    const onErrorSalesforceInformation = (error) => {
        log(['error'], 'FetchSalesforceInformation', error);
        setIsSelfServeResponse('');
        setSubmarketIdResponse('');
    };
    const [
        fetchSalesforceInformationCall,
        fetchSalesforceInformationState
    ] = useLazyQuery(FETCH_SALESFORCE_INFORMATION, {
        onCompleted: onCompleteSalesforceInformation,
        onError: onErrorSalesforceInformation
    });
    const saveSalesforceInformation = async () => {
        const result = await fetchSalesforceInformationCall({
            variables: {
                request: buildSalesforceInformationPayload({
                    ...props,
                    lypsSalesforceExperiment
                })
            }
        });
        return result?.data?.fetchSalesforceInformation;
    };
    const startUPLFlow = async ({
        pageName
    }) => {
        // this is needed because ONLY selfServe users should create an account
        await saveSalesforceInformation();
        const { pageFlow } = props;
        pageFlow.next(pageName);
    };

    useEffect(() => {
        setLoading(
            createContactState.loading
            || fetchSubmarketIdState.loading
            || fetchSalesforceInformationState.loading
        );
    }, [
        createContactState.loading,
        fetchSubmarketIdState.loading,
        fetchSalesforceInformationState.loading
    ]);

    const foundPropertyFlow = useCallback(async () => {
        const lypResult = await callPropertyCrawler();
        if (lypResult && lypResult.found) {
            const { pageFlow, setPropertyId } = props;
            setPropertyId(lypResult.propertyId);
            pageFlow.browseToNextPage(AGENTIC_LANDING_PAGE(lypResult.propertyId));
            return true;
        }
        return false;
    // eslint-disable-next-line react/destructuring-assignment
    }, [callPropertyCrawler, props.pageFlow]);

    const submitPropertyOnboarding = useCallback(async ({
        pageName
    }) => {
        if (isAgentic) {
            try {
                const propertyFound = await foundPropertyFlow();
                if (propertyFound) {
                    return;
                }
            } catch (error) {
                log(['error'], 'FoundPropertyFlow', error);
            }
        }

        const isAAP = AAPPages.includes(pageName);
        if (isAAP) {
            createJoin({
                source: JOIN_SOURCES.AAP,
                pageName
            });
        } else {
            startUPLFlow({
                pageName
            });
        }
    }, [isAgentic, foundPropertyFlow, createJoin, startUPLFlow]);
    return (
        <JoinCtx.Provider value={{
            createJoin,
            createAgenticJoin,
            loading,
            fetchSubmarketId,
            startUPLFlow,
            submitPropertyOnboarding
        }}
        >
            {children}
        </JoinCtx.Provider>
    );
}

JoinHandler.propTypes = proptypes;
JoinHandler.defaultProps = defaultProps;
const connectedJoinHandler = connect(mapStateToProps, mapDispatchToProps)(JoinHandler);

connectedJoinHandler.displayName = JOIN_HANDLER;
export default hot(module)(withPageFlow(connectedJoinHandler));
